import { Plugin, NodeSelection, TextSelection } from 'prosemirror-state';
// TODO: Universalize the App object, so import works here. Or, just keep using the window object, that's probably fine, too.
// import utils from "../common/utils";

const checkState = {
  imageNodeIsSelected(state) {
    return !!(state.selection.node && state.selection.node.type.name === 'image');
  }
};

// NOTE: It's really useful to scope the plugin here, so it can talk to itself and the instance it'll create on the state.
const ImagePickerPlugin = (components, imageSource) => {
  const utils = App.utils;
  // NOTE: The view object is not immutable, thus view.state will always be the current state of the view.
  const replaceSelectionWithImage = view => attrs => {
    const node = view.state.schema.nodes.image.createAndFill(attrs);
    const tr = view.state.tr;
    // TODO: There might be a more consistent way of achieving the current post-replacement reselection of the replaced content.
    if (tr.selection instanceof TextSelection) {
      const isLast =
        tr.doc.nodeAt(tr.selection.to) === null && tr.doc.nodeAt(tr.selection.to + 1) === null;
      // Only split if we're in some text.
      if (!tr.selection.empty) tr.split(tr.selection.from);
      // Replace the empty text selection.
      tr.replaceSelectionWith(node);
      // Get the start of the selection's current position.
      if (tr.selection instanceof TextSelection) {
        const from = tr.selection.from;
        const selection = NodeSelection.create(tr.doc, from - 2);
        tr.setSelection(selection);
      } else if (tr.selection instanceof NodeSelection) {
        const isFirst = tr.selection.from === 0;
        const from = tr.selection.from + (isLast || isFirst ? 0 : -1);
        const selection = NodeSelection.create(tr.doc, from);
        tr.setSelection(selection);
      }
    } else if (tr.selection instanceof NodeSelection && checkState.imageNodeIsSelected(tr)) {
      // Get the start of the selection's current position.
      const from = tr.selection.from;
      // Replace the node selection.
      tr.replaceSelectionWith(node);
      // Create a selection that start from where the last selection was, otherwise the selection is after the replacement.
      const selection = NodeSelection.create(tr.doc, from);
      tr.setSelection(selection);
    }
    tr.setMeta(plugin, 'setImage');
    view.dispatch(tr);
  };

  const plugin = new Plugin({
    view: view => {
      const replaceViewSelectionWithImage = replaceSelectionWithImage(view);
      const pluginState = plugin.getState(view.state);
      // ImagePicker selection replaces the view selection with the selected image.
      const hookSelectionReplacement = pluginState => {
        pluginState.imagePicker.onImageSelection(imageRecord => {
          if (imageRecord) {
            replaceViewSelectionWithImage({
              // TODO: Update to expect full image URLs, when that's a thing.
              src: imageSource(imageRecord.path, /\//.test(imageRecord.path)),
              title: imageRecord.title || '',
              alt: imageRecord.alt || '',
              id: imageRecord.id
            });
          }
        });
      };
      hookSelectionReplacement(pluginState);
      return {
        update(view, editorState) {
          const previousPluginState = plugin.getState(editorState);
          const pluginState = plugin.getState(view.state);
          // NOTE: The plugin's imagePicker shouldn't change, but we update if it ever does.
          if (pluginState.imagePicker !== previousPluginState.imagePicker) {
            hookSelectionReplacement(pluginState);
          }
        }
      };
    },
    state: {
      init({ schema, plugins }, editorState) {
        // Create the Image Picker Component
        const imagePickerEl = document.createElement('div');
        imagePickerEl.classList.add('image-picker');
        imagePickerEl.classList.add('wysiwyg-menu__image-picker');

        const imagePicker = new components.imagePicker({
          el: imagePickerEl,
          scrollIntoView: false
        });

        // TODO: Collapse the search tools, instead of deleting the button.
        imagePicker.$el.find('.collapse.button').remove();

        // Create the image upload component.
        const imageUploadEl = document.createElement('div');
        imageUploadEl.classList.add('file-upload');
        const imageUpload = new components.uploadImage({ el: imageUploadEl });

        // Create the image upload label container.
        const imageUploadLabel = document.createElement('label');
        imageUploadLabel.appendChild(document.createTextNode('Upload an Image'));
        imageUploadLabel.appendChild(imageUploadEl);

        const container = document.createElement('div');
        const modal = new components.modal({ el: container });
        container.appendChild(imagePickerEl);

        let imageSelectionCallback = () => {};
        const handleImageSelection = callback => {
          imageSelectionCallback = callback;
        };

        modal.applyClose(imagePicker.$el.find('.image-picker__close'));
        modal.$modal.addClass('wysiwyg__image-modal');

        const replacingState = utils.modifier(['adding', 'replacing'])(
          modal.$modal,
          'wysiwyg__image-modal'
        );
        replacingState('adding');

        return {
          el: container,
          imagePicker,
          imageUpload,
          modal,
          isReplacing: predicate =>
            replacingState(predicate === undefined ? undefined : predicate ? 'replacing' : 'adding')
          // handleImageSelection
        };
      },

      apply(transaction, pluginState, previousEditorState, editorState) {
        const nextState = pluginState;
        // An image node is selected.
        if (
          checkState.imageNodeIsSelected(editorState) &&
          // Only show the modal if the image was selected with a "pointer" event.
          transaction.getMeta('pointer')
        ) {
          if (!(transaction.getMeta(plugin) === 'setImage')) {
            // TODO: Update to use the image id and all the image info once we're exporting to JSON and can expect to have the id on the document.
            pluginState.imagePicker.clearImageSelection();
            pluginState.imagePicker.setImagePreview(
              imageSource(
                editorState.selection.node.attrs.src,
                /\//.test(editorState.selection.node.attrs.src)
              )
            );
          }
          pluginState.isReplacing(true);
          pluginState.modal.show();
        }
        // Something dispatched an image picker transaction.
        else if (
          transaction.getMeta(plugin) === 'show' ||
          transaction.getMeta('imagePicker') === 'show'
        ) {
          pluginState.imagePicker.clearImageSelection();
          // Set the preview image to `undefined`.
          pluginState.imagePicker.setImagePreview();
          pluginState.isReplacing(false);
          pluginState.modal.show();
        } else if (transaction.getMeta(plugin) === 'setImage') {
          // Do nothing. We're talking to ourselves.
          // TODO: Expose the constants that we create in this plugin.
          pluginState.isReplacing(true);
        }
        // There was any other kind of transaction.
        else {
          pluginState.modal.hide();
        }
        return nextState;
      }
    }
  });
  return plugin;
};

export default ImagePickerPlugin;
